import { setEventTracking } from "@/utils";
import dispatchToast from "@/utils/dispatch-toast";
import { trpc } from "@api/client";
import { useSelectedCompany } from "@flash-tecnologia/hros-web-utility";
import { cpfValidator } from "@utils/cpf-validator";
import { onlyNumbers } from "@utils/only-numbers";
import { Employee, JobRole } from "bff/src/services";
import { createContext, ReactNode, useContext, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import * as yup from "yup";
import { REQUIRED_FORM_FIELD_ERROR } from "../constants";
import { useCanMigrateEmployee } from "../hooks/use-can-migrate-employee";
import { useFindEmployeesInEconomicGroup } from "../hooks/use-find-employee-in-economic-group";
import {
  Department,
  Member,
  PermissionProfiles,
  PermissionRole,
  TrackingEvents,
} from "../types";

const INVALID_NAME_WITH_DIGITS = "Campo não pode conter números.";
const INVALID_DOCUMENT_NUMBER = "CPF inválido.";
const INVALID_DOCUMENT_NUMBER_IN_CURRENT_COMPANY =
  "CPF já cadastrado nesta empresa.";
const DOCUMENT_NUMBER_ALREADY_ADDED = "A pessoa já foi adicionada.";
const INVALID_EMAIL = "E-mail inválido.";
const INVALID_PHONE = "Celular inválido.";
const CONTACT_REQUIRED = "Preencha e-mail ou celular.";

type InvitationType = "INSTANTLY" | "SCHEDULED";

export const getValidationSchema = (currentMembers: Member[]) =>
  yup
    .object({
      documentNumber: yup
        .string()
        .required(REQUIRED_FORM_FIELD_ERROR)
        .test("valid-cpf", INVALID_DOCUMENT_NUMBER, (value) => {
          if (!value) return false;
          const digits = onlyNumbers(value);
          return cpfValidator(digits);
        })
        .test("already-exists", DOCUMENT_NUMBER_ALREADY_ADDED, (value) => {
          if (!value) return false;
          const digits = onlyNumbers(value);
          return (
            !digits || !currentMembers.some((m) => m.documentNumber === digits)
          );
        }),
      name: yup
        .string()
        .required(REQUIRED_FORM_FIELD_ERROR)
        .test("no-digits-name", INVALID_NAME_WITH_DIGITS, (value) => {
          if (!value) return true;
          const digits = onlyNumbers(value);
          return !digits.length;
        }),
      withoutEmail: yup.boolean().required(),
      email: yup.string().nullable().email(INVALID_EMAIL),
      hiringDate: yup.date().required(REQUIRED_FORM_FIELD_ERROR),
      phone: yup
        .string()
        .nullable()
        .test(
          "valid-phone",
          INVALID_PHONE,
          (value) => !value || onlyNumbers(value).length >= 10
        ),
      jobRole: yup.string().required(REQUIRED_FORM_FIELD_ERROR).nullable(),
      department: yup.string().required(REQUIRED_FORM_FIELD_ERROR).nullable(),
    })
    .test("email-or-phone-rule", "one contact is required", function (values) {
      const { withoutEmail, email, phone } = values || {};
      const errors: yup.ValidationError[] = [];

      if (withoutEmail) {
        if (!phone)
          errors.push(
            new yup.ValidationError(REQUIRED_FORM_FIELD_ERROR, values, "phone")
          );
      } else {
        if (!email && !phone) {
          errors.push(
            new yup.ValidationError(CONTACT_REQUIRED, values, "email")
          );
          errors.push(
            new yup.ValidationError(CONTACT_REQUIRED, values, "phone")
          );
        }
      }

      if (errors.length) {
        return new yup.ValidationError(errors);
      }

      return true;
    });

interface ManageMembersContextType {
  addMemberLoading: boolean;
  department?: Department;
  departmentsTotalCount: number;
  documentNumber?: string;
  email: string;
  filteredAddedMembers: { id: string; documentNumber: string; name: string }[];
  filteredDepartments: Department[];
  filteredJobRoles?: JobRole[];
  hiringDate?: Date;
  isFormLoading: boolean;
  isPermissionsDrawerOpen: boolean;
  isDeleteMemberModalOpen: boolean;
  jobRole?: JobRole;
  jobRolesTotalCount: number;
  members: Member[];
  name: string;
  permissionProfile: PermissionProfiles;
  permissionRoles?: PermissionRole[];
  phone: string;
  withoutEmail: boolean;
  invitationType?: InvitationType;
  invitationDate?: string;
  errors?: {
    documentNumber?: string;
    hiringDate?: string;
    name?: string;
    phone?: string;
    email?: string;
    jobRole?: string;
    department?: string;
  };

  isFormDisabled: boolean;
  canMigrateEmployee?: boolean;
  setIsFormDisabled: (value: boolean) => void;
  willMigrate: boolean;
  setWillMigrate: (value: boolean) => void;

  getEmailHelperText: () => string;
  onAddMemberClick: () => Promise<void>;
  onNameChange: (value: string) => void;
  onDeleteMemberClick: (memberId: string) => void;
  onDeleteMemberModalClose: () => void;
  onDeleteMemberSubmit: () => void;
  onDepartmentBlur: () => void;
  onDepartmentChange: (value: Department) => void;
  onDocumentNumberBlur: () => void;
  onDocumentNumberChange: (value: string) => void;
  onEmailBlur: () => void;
  onEmailChange: (value: string) => void;
  onHiringDateBlur: () => void;
  onHiringDateChange: (value?: Date) => void;
  onJobRoleBlur: () => void;
  onJobRoleChange: (value: JobRole) => void;
  onNameBlur: () => void;
  onInvitationTypeChange: (value: InvitationType) => void;
  onInvitationDateChange: (value: string | undefined) => void;
  onPermissionsDrawerClose: () => void;
  onPermissionsDrawerOpen: () => void;
  onPermissionsDrawerSubmit: (
    permissionProfile: PermissionProfiles,
    permissionRoles?: PermissionRole[]
  ) => void;
  onPhoneBlur: () => void;
  onPhoneChange: (value: string) => void;
  onSearchDepartmentTermChange: (value?: string) => void;
  onSearchMembersTermChange: (value?: string) => void;
  onSearchJobRoleTermChange: (value?: string) => void;
  onWithoutEmailChange: (value: boolean) => void;
}

const ManageMembersContext = createContext<ManageMembersContextType | null>(
  null
);

interface ManageMembersProviderProps {
  children: ReactNode;
}

export const ManageMembersProvider: React.FC<ManageMembersProviderProps> = ({
  children,
}) => {
  const [members, setMembers] = useState<Member[]>([]);
  const [addMemberLoading, setAddMemberLoading] = useState<boolean>(false);
  const [memberIdToDelete, setMemberIdToDelete] = useState<string>();

  const [documentNumber, setDocumentNumber] = useState<string>();
  const [hiringDate, setHiringDate] = useState<Date | undefined>();
  const [name, setName] = useState<string>("");
  const [phone, setPhone] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [withoutEmail, setWithoutEmail] = useState<boolean>(false);
  const [jobRole, setJobRole] = useState<JobRole | undefined>();
  const [department, setDepartment] = useState<Department | undefined>();
  const [permissionProfile, setPermissionProfile] =
    useState<PermissionProfiles>(PermissionProfiles.EMPLOYEE);
  const [permissionRoles, setPermissionRoles] = useState<PermissionRole[]>();
  const [invitationType, setInvitationType] = useState<InvitationType>();
  const [invitationDate, setInvitationDate] = useState<string>();
  const [searchJobRoleTerm, setSearchJobRoleTerm] = useState<string>();
  const [searchDepartmentTerm, setSearchDepartmentTerm] = useState<string>();
  const [searchMembersTerm, setSearchMembersTerm] = useState<string>();
  const [errors, setErrors] = useState<{
    documentNumber?: string;
    name?: string;
    phone?: string;
    email?: string;
    hiringDate?: string;
    jobRole?: string;
    department?: string;
  }>();

  const [employeeIdForTransfer, setEmployeeIdForTransfer] = useState("");
  const [isFormDisabled, setIsFormDisabled] = useState<boolean>(false);
  const [canMigrateEmployee, setCanMigrateEmployee] = useState<boolean>();
  const [willMigrate, setWillMigrate] = useState<boolean>(true);

  const [isPermissionsDrawerOpen, setIsPermissionsDrawerOpen] = useState(false);
  const [isDeleteMemberModalOpen, setIsDeleteMemberModalOpen] = useState(false);

  const { selectedCompany } = useSelectedCompany();

  const {
    data: companyDepartments,
    isLoading: isGetCompanyDepartmentsLoading,
  } = trpc.getDepartments.useQuery(
    { companyId: selectedCompany.id },
    { enabled: !!selectedCompany.id }
  );
  const { data: jobRoles, isLoading: isGetJobRolesLoading } =
    trpc.getJobRoles.useQuery({
      companyId: selectedCompany.id,
    });
  const { canMigrate } = useCanMigrateEmployee();
  const { findEmployeesInEconomicGroup } = useFindEmployeesInEconomicGroup({
    options: {
      onFound: async (employees) => {
        const sameCompanyEmployee = employees.filter(
          (employee) => employee.companyId === selectedCompany.id
        );
        sameCompanyEmployee.length
          ? await onFoundEmployee(sameCompanyEmployee[0])
          : await onFoundEmployee(employees[0]);
      },
      onNotFound: () => onNotFoundEmployee(),
    },
  });

  const onFoundEmployee = async (employee: Employee) => {
    setName(employee.name);
    if (employee.email) setEmail(employee.email);
    if (employee.phoneNumber) setPhone(employee.phoneNumber);

    if (employee.companyId === selectedCompany.id) {
      setIsFormDisabled(true);
      setErrors((prevError) => ({
        ...prevError,
        documentNumber: INVALID_DOCUMENT_NUMBER_IN_CURRENT_COMPANY,
      }));
      return;
    }

    const employeeCanMigrate = await canMigrate({ employeeId: employee.id });

    if (employeeCanMigrate.canMigrate) {
      setEmployeeIdForTransfer(employee.id);
      setCanMigrateEmployee(true);
      setIsFormDisabled(true);
      return;
    }

    setIsFormDisabled(false);
    setCanMigrateEmployee(false);
    setWillMigrate(false);
  };

  const onNotFoundEmployee = () => {
    setIsFormDisabled(false);
    setCanMigrateEmployee(undefined);
  };

  const getEmailHelperText = () => {
    if (isFormDisabled) {
      return "";
    }

    if (withoutEmail) {
      return "O convite pra acesso será enviado para o celular";
    }

    return (
      errors?.email ?? "O convite pra acesso será enviado para esse e-mail"
    );
  };

  const handleAddMemberClick = async () => {
    setEventTracking(TrackingEvents.SETUP_ADD_INDIVIDUAL_EMPLOYEE_SAVE_CLICKED);
    setAddMemberLoading(true);
    let newErrors: Record<string, string> = {};

    const isMigrating = canMigrateEmployee && willMigrate;

    if (isMigrating) {
      setMembers((prev) => [
        ...prev,
        {
          id: employeeIdForTransfer,
          documentNumber: documentNumber ?? "",
          name: name ?? "",
          phone: phone ?? "",
          email: email ?? "",
          jobRoleId: jobRole?.id ?? "",
          departmentId: department?.id ?? "",
          permissionRoleProfile: permissionProfile ?? "",
          permissionRoleIds: permissionRoles?.map(({ id }) => id) ?? [],
          type: "MIGRATE",
        },
      ]);
      resetForm();
      setAddMemberLoading(false);
      return;
    }

    try {
      await getValidationSchema(members).validate(
        {
          documentNumber,
          hiringDate,
          name,
          phone,
          email,
          withoutEmail,
          jobRole: jobRole?.id,
          department: department?.id,
        },
        { abortEarly: false }
      );
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        newErrors = error.inner.reduce((acc, curr) => {
          if (curr.path) acc[curr.path] = curr.message;
          return acc;
        }, {} as Record<string, string>);
        setErrors(newErrors);
      }
    } finally {
      setAddMemberLoading(false);
    }

    if (Object.values(newErrors).length) {
      return;
    }

    if (
      !documentNumber ||
      !name ||
      !jobRole ||
      !department ||
      !permissionProfile ||
      !hiringDate
    ) {
      return;
    }

    setMembers((prev) => [
      ...prev,
      {
        id: uuidv4(),
        documentNumber,
        name,
        phone,
        email,
        hiringDate,
        jobRoleId: jobRole.id,
        departmentId: department.id,
        permissionRoleProfile: permissionProfile,
        permissionRoleIds: permissionRoles?.map(({ id }) => id) ?? [],
        type: "CREATE",
      },
    ]);
    resetForm();
  };

  const handleDeleteMemberClick = (memberId: string) => {
    setEventTracking(
      TrackingEvents.SETUP_ADD_INDIVIDUAL_EMPLOYEE_DELETE_CLICKED
    );
    setMemberIdToDelete(memberId);
    setIsDeleteMemberModalOpen(true);
  };

  const handleDeleteMemberModalClose = () => setIsDeleteMemberModalOpen(false);

  const handleDeleteMemberSubmit = () => {
    setMembers((prev) => prev.filter(({ id }) => id !== memberIdToDelete));
    setMemberIdToDelete(undefined);
    setIsDeleteMemberModalOpen(false);
  };

  const handleDepartmentBlur = () => {
    if (!department) {
      setErrors((prev) => ({ ...prev, department: REQUIRED_FORM_FIELD_ERROR }));
    } else {
      setErrors((prev) => ({ ...prev, department: undefined }));
    }
  };

  const handleDepartmentChange = (value: Department) => {
    setDepartment(value);

    setErrors((prev) => ({ ...prev, department: undefined }));
  };

  const handleDocumentNumberBlur = () => {
    setIsFormDisabled(false);
    if (!documentNumber) {
      return setErrors((prev) => ({
        ...prev,
        documentNumber: REQUIRED_FORM_FIELD_ERROR,
      }));
    }

    if (!cpfValidator(documentNumber)) {
      return setErrors((prev) => ({
        ...prev,
        documentNumber: INVALID_DOCUMENT_NUMBER,
      }));
    }

    const documentNumberDigits = onlyNumbers(documentNumber);
    const alreadyAdded = members.find(
      (member) => member.documentNumber === documentNumberDigits
    );
    if (alreadyAdded) {
      return setErrors((prev) => ({
        ...prev,
        documentNumber: DOCUMENT_NUMBER_ALREADY_ADDED,
      }));
    }

    findEmployeesInEconomicGroup(documentNumberDigits);
    setErrors((prev) => ({ ...prev, documentNumber: undefined }));
  };

  const handleDocumentNumberChange = (value: string) => {
    const digits = onlyNumbers(value);
    if (digits?.length > 11) {
      return;
    }
    setDocumentNumber(digits);
  };

  const handleEmailBlur = () => {
    if (withoutEmail) {
      return;
    }

    if (!email && !phone) {
      setErrors((prev) => ({
        ...prev,
        email: REQUIRED_FORM_FIELD_ERROR,
        phone: REQUIRED_FORM_FIELD_ERROR,
      }));
    } else {
      setErrors((prev) => ({ ...prev, phone: undefined, email: undefined }));
    }
  };

  const handleEmailChange = (value: string) => setEmail(value);

  const handleHiringDateBlur = () => {
    if (!hiringDate) {
      setErrors((prev) => ({ ...prev, hiringDate: REQUIRED_FORM_FIELD_ERROR }));
    } else {
      setErrors((prev) => ({ ...prev, hiringDate: undefined }));
    }
  };

  const handleHiringDateChange = (value?: Date) => {
    setHiringDate(value);

    setErrors((prev) => ({ ...prev, hiringDate: undefined }));
  };

  const handleJobRoleBlur = () => {
    if (!jobRole) {
      setErrors((prev) => ({ ...prev, jobRole: REQUIRED_FORM_FIELD_ERROR }));
    } else {
      setErrors((prev) => ({ ...prev, jobRole: undefined }));
    }
  };

  const handleJobRoleChange = (value: JobRole) => {
    setJobRole(value);

    setErrors((prev) => ({ ...prev, jobRole: undefined }));
  };

  const handleChangeName = (value: string) => setName(value);

  const handleNameBlur = () => {
    if (!name) {
      setErrors((prev) => ({ ...prev, name: REQUIRED_FORM_FIELD_ERROR }));
    } else if (!!onlyNumbers(name)?.length) {
      setErrors((prev) => ({ ...prev, name: INVALID_NAME_WITH_DIGITS }));
    } else {
      setErrors((prev) => ({ ...prev, name: undefined }));
    }
  };

  const handlePermissionsDrawerClose = () => {
    setIsPermissionsDrawerOpen(false);
  };

  const handlePermissionsDrawerOpen = () => {
    setEventTracking(
      TrackingEvents.SETUP_ADD_INDIVIDUAL_EMPLOYEE_PROFILE_CLICKED
    );
    setIsPermissionsDrawerOpen(true);
  };

  const handlePermissionsDrawerSubmit = (
    permissionProfile: PermissionProfiles,
    permissionRoles?: PermissionRole[]
  ) => {
    setPermissionProfile(permissionProfile);
    setPermissionRoles(permissionRoles);
    dispatchToast({
      type: "success",
      content: "Permissionamento alterado com sucesso!",
    });
    setIsPermissionsDrawerOpen(false);
  };

  const handlePhoneBlur = () => {
    if (withoutEmail && !phone) {
      setErrors((prev) => ({
        ...prev,
        phone: REQUIRED_FORM_FIELD_ERROR,
      }));
    } else if (!email && !phone) {
      setErrors((prev) => ({
        ...prev,
        email: REQUIRED_FORM_FIELD_ERROR,
        phone: REQUIRED_FORM_FIELD_ERROR,
      }));
    } else {
      setErrors((prev) => ({ ...prev, phone: undefined, email: undefined }));
    }
  };

  const handlePhoneChange = (value: string) => {
    const digits = onlyNumbers(value);
    if (digits?.length > 11) {
      return;
    }
    setPhone(digits);
  };

  const handleWithoutEmailChange = (value: boolean) => {
    setWithoutEmail(value);

    if (value) {
      setEmail("");
      setErrors((prev) => ({ ...prev, email: undefined }));
    }
  };

  const addedMembers = members
    ? members.map(({ id, documentNumber, name }) => {
        return {
          id,
          documentNumber,
          name,
        };
      })
    : [];

  const filteredAddedMembers = searchMembersTerm
    ? addedMembers.filter((member) =>
        member.name.toLowerCase().includes(searchMembersTerm.toLowerCase())
      )
    : addedMembers;

  const filteredJobRoles = searchJobRoleTerm
    ? jobRoles?.filter((role) =>
        role.name.toLowerCase().includes(searchJobRoleTerm.toLowerCase())
      )
    : jobRoles;

  const filteredDepartments = searchDepartmentTerm
    ? companyDepartments?.filter((department) =>
        department.name
          .toLowerCase()
          .includes(searchDepartmentTerm.toLowerCase())
      )
    : companyDepartments;

  const resetForm = () => {
    setDocumentNumber("");
    setHiringDate(undefined);
    setName("");
    setPhone("");
    setEmail("");
    setWithoutEmail(false);
    setJobRole(undefined);
    setDepartment(undefined);
    setPermissionProfile(PermissionProfiles.EMPLOYEE);
    setPermissionRoles(undefined);
    setErrors({});
    setWillMigrate(true);
    setIsFormDisabled(false);
    setCanMigrateEmployee(undefined);
  };

  const values = {
    addMemberLoading,
    department,
    departmentsTotalCount: companyDepartments?.length ?? 0,
    documentNumber,
    email,
    filteredAddedMembers,
    filteredDepartments: filteredDepartments ?? [],
    filteredJobRoles: filteredJobRoles ?? [],
    hiringDate,
    isFormLoading: isGetCompanyDepartmentsLoading || isGetJobRolesLoading,
    isPermissionsDrawerOpen,
    isDeleteMemberModalOpen,
    jobRole,
    jobRolesTotalCount: jobRoles?.length ?? 0,
    invitationType,
    invitationDate,
    members,
    name,
    permissionProfile,
    permissionRoles,
    phone,
    withoutEmail,
    errors,

    isFormDisabled,
    canMigrateEmployee,
    setIsFormDisabled,
    willMigrate,
    setWillMigrate,

    getEmailHelperText,
    onAddMemberClick: handleAddMemberClick,
    onDeleteMemberClick: handleDeleteMemberClick,
    onDeleteMemberModalClose: handleDeleteMemberModalClose,
    onDeleteMemberSubmit: handleDeleteMemberSubmit,
    onDepartmentBlur: handleDepartmentBlur,
    onDepartmentChange: handleDepartmentChange,
    onDocumentNumberBlur: handleDocumentNumberBlur,
    onDocumentNumberChange: handleDocumentNumberChange,
    onEmailBlur: handleEmailBlur,
    onEmailChange: handleEmailChange,
    onHiringDateBlur: handleHiringDateBlur,
    onHiringDateChange: handleHiringDateChange,
    onJobRoleBlur: handleJobRoleBlur,
    onJobRoleChange: handleJobRoleChange,
    onNameBlur: handleNameBlur,
    onNameChange: handleChangeName,
    onInvitationTypeChange: setInvitationType,
    onInvitationDateChange: setInvitationDate,
    onPermissionsDrawerClose: handlePermissionsDrawerClose,
    onPermissionsDrawerOpen: handlePermissionsDrawerOpen,
    onPermissionsDrawerSubmit: handlePermissionsDrawerSubmit,
    onPhoneBlur: handlePhoneBlur,
    onPhoneChange: handlePhoneChange,
    onSearchDepartmentTermChange: setSearchDepartmentTerm,
    onSearchMembersTermChange: setSearchMembersTerm,
    onSearchJobRoleTermChange: setSearchJobRoleTerm,
    onWithoutEmailChange: handleWithoutEmailChange,
  };

  return (
    <ManageMembersContext.Provider value={values}>
      {children}
    </ManageMembersContext.Provider>
  );
};

export const useManageMembersForm = () => {
  const context = useContext(ManageMembersContext);
  if (!context) {
    throw new Error(
      "useManageMembersForm must be used within a ManageMembersProvider"
    );
  }
  return context;
};
